import { baseClassLocalName } from '@/lib/baseClassColors';
import type { VdbEdge, VdbTopClass } from '@/lib/useVdb';

// The ontology Viz draws one of two structures (ruled 2026-10-07: clean
// structure first — no node overlaps, minimal edge crossings; see
// Documentation/development/archive/VIZ_STRUCTURE_SPIKE_2026_10_07.md).
//
// - hierarchy: every class and the subclass edges, a layered tree.
// - relationships: the classes that take part in an object property and the
//   property links among them — the hubs the data clusters on, with the
//   subclass tree left out, because drawn together the links cross the tree
//   (198 crossings on the FMS ontology) and drawn apart each is clean.
export type StructureView = 'hierarchy' | 'relationships';

export interface StructureNode {
  id: string;
  width: number;
  height: number;
}

export interface StructureEdge {
  id: string;
  source: string;
  target: string;
}

export interface Placed {
  id: string;
  /** Centre coordinates. */
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The classes and edges a view draws. */
export function viewElements(
  view: StructureView,
  classes: VdbTopClass[],
  hierarchy: VdbEdge[],
  schemaEdges: VdbEdge[],
): { classes: VdbTopClass[]; edges: VdbEdge[] } {
  const known = new Set(classes.map((c) => c.uri));
  if (view === 'hierarchy') {
    const edges = hierarchy.filter((e) => known.has(e.source_uri) && known.has(e.target_uri));
    // A base class of the fixed frame with nothing under it is scaffolding,
    // not a level of the user's model; it stays off the tree.
    const shown = classes.filter(
      (c) => !(c.is_top_level && c.subclass_count === 0 && baseClassLocalName(c.uri) !== null),
    );
    return { classes: shown, edges };
  }
  const links = schemaEdges.filter(
    (e) => e.source_uri !== e.target_uri && known.has(e.source_uri) && known.has(e.target_uri),
  );
  const linked = new Set(links.flatMap((e) => [e.source_uri, e.target_uri]));
  return { classes: classes.filter((c) => linked.has(c.uri)), edges: links };
}

/**
 * The view an ontology opens on: relationships when object properties join
 * more than a handful of classes, hierarchy otherwise. A choice the user can
 * change on the page.
 */
export function defaultView(classes: VdbTopClass[], schemaEdges: VdbEdge[]): StructureView {
  const linked = viewElements('relationships', classes, [], schemaEdges).classes.length;
  return linked > 5 ? 'relationships' : 'hierarchy';
}

/**
 * The edges a view's layout flows along. A subclass edge points child to
 * parent, and the tree reads parent above child, so the hierarchy's edges are
 * reversed for layering; the canvas keeps drawing them as stored.
 */
export function layoutEdges(view: StructureView, edges: VdbEdge[]): StructureEdge[] {
  return edges.map((e) =>
    view === 'hierarchy'
      ? { id: e.edge_id, source: e.target_uri, target: e.source_uri }
      : { id: e.edge_id, source: e.source_uri, target: e.target_uri },
  );
}

const LAYERED_OPTIONS: Record<string, string> = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
  'elk.layered.thoroughness': '20',
  'elk.spacing.nodeNode': '36',
  'elk.layered.spacing.nodeNodeBetweenLayers': '80',
  'elk.spacing.edgeNode': '24',
  // A forest shares one layering: every root on the top row, rather than one
  // tree stacked under another.
  'elk.separateConnectedComponents': 'false',
};

/**
 * Deterministic positions for a view: a layered layout with crossing
 * minimization, nodes in their own slots so none overlap. The library loads
 * on first use; it is large and only the Viz needs it.
 */
export async function layoutStructure(
  nodes: StructureNode[],
  edges: StructureEdge[],
): Promise<Placed[]> {
  if (nodes.length === 0) return [];
  const { default: ELK } = await import('elkjs/lib/elk.bundled.js');
  const elk = new ELK();
  const ids = new Set(nodes.map((n) => n.id));
  const graph = {
    id: 'root',
    layoutOptions: LAYERED_OPTIONS,
    children: nodes.map((n) => ({ id: n.id, width: n.width, height: n.height })),
    edges: edges
      .filter((e) => ids.has(e.source) && ids.has(e.target) && e.source !== e.target)
      .map((e) => ({ id: e.id, sources: [e.source], targets: [e.target] })),
  };
  const out = await elk.layout(graph);
  const size = new Map(nodes.map((n) => [n.id, n]));
  return (out.children ?? []).map((c) => {
    const n = size.get(c.id);
    const width = n?.width ?? c.width ?? 0;
    const height = n?.height ?? c.height ?? 0;
    return { id: c.id, x: (c.x ?? 0) + width / 2, y: (c.y ?? 0) + height / 2, width, height };
  });
}

/** Pairs of node boxes that intersect, with `padding` around each box. */
export function countOverlaps(placed: Placed[], padding = 0): number {
  let n = 0;
  for (let i = 0; i < placed.length; i++) {
    for (let j = i + 1; j < placed.length; j++) {
      const a = placed[i]!;
      const b = placed[j]!;
      const dx = Math.abs(a.x - b.x);
      const dy = Math.abs(a.y - b.y);
      if (dx < (a.width + b.width) / 2 + padding && dy < (a.height + b.height) / 2 + padding) n++;
    }
  }
  return n;
}

function orientation(p: Placed, q: Placed, r: Placed): number {
  return Math.sign((q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x));
}

/**
 * Pairs of straight edges that cross, excluding pairs that share an
 * endpoint. The canvas draws straight lines, so this is what the user sees.
 */
export function countCrossings(placed: Placed[], edges: StructureEdge[]): number {
  const at = new Map(placed.map((p) => [p.id, p]));
  const segs = edges
    .filter((e) => at.has(e.source) && at.has(e.target) && e.source !== e.target)
    .map((e) => ({ e, a: at.get(e.source)!, b: at.get(e.target)! }));
  let n = 0;
  for (let i = 0; i < segs.length; i++) {
    for (let j = i + 1; j < segs.length; j++) {
      const s = segs[i]!;
      const t = segs[j]!;
      const shared =
        s.e.source === t.e.source ||
        s.e.source === t.e.target ||
        s.e.target === t.e.source ||
        s.e.target === t.e.target;
      if (shared) continue;
      const o1 = orientation(s.a, s.b, t.a);
      const o2 = orientation(s.a, s.b, t.b);
      const o3 = orientation(t.a, t.b, s.a);
      const o4 = orientation(t.a, t.b, s.b);
      if (o1 !== o2 && o3 !== o4 && o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0) n++;
    }
  }
  return n;
}

/** The one line the canvas says about a drawing's structure. */
export function structureSummary(
  view: StructureView,
  classes: number,
  edges: number,
  overlaps: number,
  crossings: number,
): string {
  const what =
    view === 'hierarchy'
      ? `Hierarchy: ${classes} class${classes === 1 ? '' : 'es'}, ${edges} subclass link${edges === 1 ? '' : 's'}.`
      : `Relationships: ${classes} class${classes === 1 ? '' : 'es'}, ${edges} link${edges === 1 ? '' : 's'}.`;
  const clean =
    overlaps === 0
      ? `No overlaps, ${crossings} crossing${crossings === 1 ? '' : 's'}.`
      : `${overlaps} overlap${overlaps === 1 ? '' : 's'}, ${crossings} crossing${crossings === 1 ? '' : 's'}.`;
  return `${what} ${clean}`;
}
